import { sendAdjustAddToCartEvent } from '../Common/Utility/Adjust';
import {
  ADJUST_EVENT_TOKENS,
  ASTER_PRODUCT_AVAILABILITY,
  ASTER_PRODUCT_TYPES,
  DELIVERY_TYPES,
  ONE_ASTER_CATEGORY,
  PAYMENT_CURRENCY,
} from '@common/Utility/constants';
import { sendEventToSmartLook } from '../Common/Utility/smartlook';
import { getAttributeItemFromArray, getProductPath } from '../Common/Utility/common-utils';
import { dataLayerSendEvent as sendEventData } from '../Common/Utility/analytics-utils';
import { getCurrency, MY_ASTER_CATEGORY } from '@common/Constants/analytics';
import { getRegionCodeFromUrl } from '../../i18n';

/**
 * call data layer method of tagmanager and pass requeired data and event
 * @param {*} eventName Name of event
 * @param {*} data data object to be passed to gtm
 *
 * epharmacy_page_view,
 * banner_slider_item_click, banner_slider_swipe,
 * category_item_clicked,
 * brand_item_clicked, brand_slider_swipe,
 * add_product_to_cart
 */
export const dataLayerSendEvent = (eventName, data) => {
  sendEventData(eventName, data);
};

export const getCurrencyFromRegion = () => {
  const regionCode = getRegionCodeFromUrl();
  return getCurrency(regionCode);
}

export const dataLayerSendEpharmacyPageViewEvent = ({ screenName, pageType, referrerPage, pageId, ...rest }) => {
  const params = {
    screen_name: screenName || null,
    myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
    page_type: pageType,
    referrer_page: referrerPage,
    ...rest,
  };

  if (pageId) {
    params.page_id = pageId;
  }

  dataLayerSendEvent('page_view', params);
};

export const dataLayerSendPageViewEvent = (eventName, siteWideData, userLogin = false, data) => {
  const userArea = localStorage.getItem('userArea');
  const userZone = localStorage.getItem('userZone');
  const dataLayerOptions = {
    page_type: siteWideData?.page_type,
    screen_name: siteWideData?.screen_name,
    area: userArea,
    pharmacy_zone_id: String(userZone)?.split(','),
    user_state: userLogin ? 'Logged In' : 'Guest',
    oneaster_category: ONE_ASTER_CATEGORY.PHARMACY,
    ...data,
  };
  dataLayerSendEvent(eventName, dataLayerOptions);
};

const buildInsiderBasketProps = (itemCategories, product, specialPrice) => ({
  taxonomy: [...Object.keys(itemCategories).map((item) => itemCategories[item])],
  unit_sale_price: specialPrice !== 0 ? specialPrice : parseFloat(product?.price),
  product_url: product?.product_url  || `${location?.origin}${getProductPath(product)}`,
  stock: product?.available_stock ? product?.available_stock  : product?.quantity,
  product_image_url: product?.image_url,
});

/**
 *
 * @param {*} product product object should have all details of product
 * @param {*} productItemExtraData extra information that need to merge with product item data
 * @returns analytics item object with given product information
 */
export const getProductItemObject = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
      }
      return i;
    });
  }
  const specialPrice = parseFloat(product?.sale_price) || parseFloat(product?.specialPrice)|| 0;
  let discount = product?.offer_badge;
  
  if (product?.offer_badge && specialPrice > 0 && product?.price) {
    discount = parseFloat(product?.price) - specialPrice;
  }

  return {
    item_name: product?.name,
    item_id: product?.sku,
    price: specialPrice || product?.price,
    item_brand: product?.brand_details?.brandName || getAttributeItemFromArray(product?.customAttribute, "brandName"),
    item_variant: null,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    quantity: 1,
    item_list_id:null,
    discount: specialPrice > 0 ? parseFloat(discount?.toFixed(2)) : 0,
    index: product?.index,
    ...itemCategories,
    ...buildInsiderBasketProps(itemCategories, product, specialPrice)
  };
};

export const getViewItemListBrand = (product) => {
  return {
    brand_name: product?.title,
    brand_id: product?.id,
  };
};

export const getViewItemListCategory = (product) => {
  return {
    item_name: product?.name || product?.categoryName,
    item_id: product?.categoryId || product?.id,
    item_url: product?.linkWeb,
  };
};

export const getProductItemObjectSelect = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
      }
      return i;
    });
  }
  
  const specialPrice = parseFloat(product?.special_price) || parseFloat(product?.specialPrice)|| 0;
  let discount = product?.badgeDetails?.offerBadge;
  
  if (product?.badgeDetails?.offerBadge && specialPrice > 0 && product?.price) {
    discount = parseFloat(product?.price) - specialPrice;
  }
  return {
    item_name: product?.name,
    item_id: product?.sku,
    price: specialPrice || product?.price,
    item_brand: product?.brand || getAttributeItemFromArray(product?.customAttribute, "brandName"),
    discount: specialPrice > 0 ? parseFloat(discount?.toFixed(2)) : 0,
    item_variant: null,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    item_list_id:null,
  };
};

export const getProductItemObjectEvent = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else {
          if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
        }
      }
      return i;
    });
  }
  let specialPrice = 0;
  let discount = product?.badgeDetails?.offerBadge;
  const price = product?.price;
  if (product?.badgeDetails?.offerBadge && product?.specialPrice && product?.specialPrice > 0 && product?.price) {
    discount = product?.price - product?.specialPrice;
  }

  if (product?.special_price)
    specialPrice = product?.special_price ? parseFloat(product?.special_price) : parseFloat(price);
  if (product?.specialPrice)
    specialPrice = product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(price);

  return {
    item_name: product?.name,
    total_discount: null,
    item_id: product?.sku,
    item_brand: getAttributeItemFromArray(product?.customAttribute, "brandName") || product?.brand,
    item_variant: null,
    discount: product?.specialPrice ? parseFloat(discount?.toFixed(2)) : 0,
    index: product?.index,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    item_list_id:null,
    availability: product?.inStock ? "in stock" : "out of stock",
    express_availability:product?.express ? 'true' : 'false',
    delivery_promise: product?.deliverBy,
  };
};

export const getProductItemObjectEventFillers = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else {
          if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
        }
      }
      return i;
    });
  }
  let specialPrice = 0;
  let discount = product?.badgeDetails?.offerBadge;
  const price = product?.price;
  if (product?.badgeDetails?.offerBadge && product?.specialPrice && product?.specialPrice > 0 && product?.price) {
    discount = product?.price - product?.specialPrice;
  }

  if (product?.special_price)
    specialPrice = product?.special_price ? parseFloat(product?.special_price) : parseFloat(price);
  if (product?.specialPrice)
    specialPrice = product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(price);

  return {
    item_name: product?.name,
    item_id: product?.sku,
    price:product?.specialPrice ? parseFloat(product?.specialPrice) :product?.special_price ? parseFloat(product?.special_price) :parseFloat(product?.price),
    item_brand: getAttributeItemFromArray(product?.customAttribute, "brandName") || product?.brandDetails?.brandName,
    discount: 0,
    item_variant: null,
    index: product?.index,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    item_list_id:null,
  };
};

export const getProductItemObjectAddToCartEvent = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else {
          if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
        }
      }
      return i;
    });
  }
  const specialPrice = parseFloat(product?.special_price) || parseFloat(product?.specialPrice) || 0;
  let discount = product?.badgeDetails?.offerBadge;

  if (product?.badgeDetails?.offerBadge && specialPrice > 0 && product?.price) {
    discount = parseFloat(product?.price) - specialPrice;
  }

  return {
    item_name: product?.name,
    item_id: product?.sku,
    price: product?.sale_price || product?.base_price || specialPrice || parseFloat(product?.price),
    item_brand: product?.brand_details?.brandName || getAttributeItemFromArray(product?.customAttribute, 'brandName') || product?.brandDetails?.brandName,
    discount: specialPrice > 0 ? parseFloat(discount?.toFixed(2)) : 0,
    item_variant: null,
    index: 0,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    item_list_id:null,
  };
};

export const getProductItemObjectEventFillersOptics = (products) => {
  let itemsArr = [];
  products?.forEach((product) => {
    const itemCategories = {};
    if (product?.categoryDetails) {
      product?.categoryDetails?.map((category, i) => {
        if (i < 5) {
          if (i === 0) {
            itemCategories.item_category = category.categoryName;
          } else {
            if (product?.categoryDetails[i - 1]) {
              itemCategories[`item_category${i + 1}`] = category.categoryName;
            }
          }
        }
        return i;
      });
    }
    let specialPrice = 0;
    let discount = product?.badgeDetails?.offerBadge;
    const price = product?.price;
    if (product?.badgeDetails?.offerBadge && product?.specialPrice && product?.specialPrice > 0 && product?.price) {
      discount = product?.price - product?.specialPrice;
    }

    if (product?.special_price)
      specialPrice = product?.special_price ? parseFloat(product?.special_price) : parseFloat(price);
    if (product?.specialPrice)
      specialPrice = product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(price);

    itemsArr.push({
      item_name: product?.name,
      item_id: product?.sku,
      price: product?.specialPrice
        ? parseFloat(product?.specialPrice)
        : product?.special_price
        ? parseFloat(product?.special_price)
        : parseFloat(product?.price),
      item_brand:
        getAttributeItemFromArray(product?.customAttribute, 'brandName') || product?.brandDetails?.brandName || '',
      discount: 0,
      item_variant: null,
      quantity: 1,
      index: product?.index,
      ...itemCategories,
      currency: getCurrencyFromRegion(),
      item_list_id: null,
    });
  });

  return itemsArr;
};

export const getProductItemObjectEventFillersSelect = (product, deliverBy) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
      }
      return i;
    });
  }

  const specialPrice = parseFloat(product?.special_price) || parseFloat(product?.specialPrice)|| 0;
  let discount = product?.badgeDetails?.offerBadge;
  
  if (product?.badgeDetails?.offerBadge && specialPrice > 0 && product?.price) {
    discount = parseFloat(product?.price) - specialPrice;
  }
  

  return {
    item_name: product?.name,
    item_id: product?.sku,
    price: product?.sale_price || product?.base_price || parseFloat(product?.specialPrice) || parseFloat(product?.special_price) || parseFloat(product?.price),
    item_brand: product?.brand_details?.brandName || product?.brand || product?.brandDetails?.brandName,
    discount: product?.discount?.value || 0,
    item_variant: null,
    delivery_promise: deliverBy || product?.deliverBy || null,
    express_availability: product?.express_bool || product?.express ? 'true' : 'false',
    product_type: product?.product_type === 'Optics' ? 'Optics' : ASTER_PRODUCT_TYPES.PHARMACY,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    ...buildInsiderBasketProps(itemCategories, product, product?.sale_price)
  };
};

export const getProductItemObjectEventFillersSelectNew = (product) => {
  let discount = product?.discount?.value || 0;
  return {
    item_name: product?.name,
    item_id: product?.sku,
    price: parseFloat(product?.sale_price),
    quantity: parseFloat(product?.qty),
    item_brand: product?.brand_details?.brandName,
    discount: discount,
    item_variant: null,
    delivery_promise: product?.deliver_by,
    product_type: product?.product_type === 'Optics' ? 'Optics' : ASTER_PRODUCT_TYPES.PHARMACY,
    bundle_deal_availability: product?.is_bundled ? true : false,
    express_availability: product?.delivery_type === DELIVERY_TYPES.EXPRESS ? 'true' : 'false',
    item_delivery_promise: product?.deliver_by,
    item_product_type: product?.product_type === 'Optics' ? 'Optics' : ASTER_PRODUCT_TYPES.PHARMACY,
    item_bundle_deal_availability: product?.is_bundled ? true : false,
    item_express_availability: product?.delivery_type === DELIVERY_TYPES.EXPRESS ? 'true' : 'false',
    currency: getCurrencyFromRegion(),
  };
}

export const getProductItemObjectEventFbtUpselling = (product) => {
  const itemCategories = {};
  if (product?.categoryDetails) {
    product?.categoryDetails?.map((category, i) => {
      if (i < 5) {
        if (i === 0) {
          itemCategories.item_category = category.categoryName;
        } else {
          if (product?.categoryDetails[i - 1]) {
            itemCategories[`item_category${i + 1}`] = category.categoryName;
          }
        }
      }
      return i;
    });
  }
  let specialPrice = 0;
  let discount = product?.badgeDetails?.offerBadge;
  const price = product?.price;
  if (product?.badgeDetails?.offerBadge && product?.specialPrice && product?.specialPrice > 0 && product?.price) {
    discount = product?.price - product?.specialPrice;
  }

  if (product?.special_price)
    specialPrice = product?.special_price ? parseFloat(product?.special_price) : parseFloat(price);
  if (product?.specialPrice)
    specialPrice = product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(price);

  return {
    item_name: product?.name,
    item_id: product?.sku,
    price:product?.special_price ? parseFloat(product?.special_price) :parseFloat(product?.price),
    item_brand: getAttributeItemFromArray(product?.customAttribute, "brandName") || product?.brand,
    discount: 0,
    delivery_promise: product?.deliverByV2 || product?.deliverBy || null,
    availability: product?.outOfStock ? 'out of stock' : 'in stock',
    express_availability: product?.express_bool || product?.express ? 'true' : 'false',
    item_variant: null,
    quantity:1,
    index: product?.index +1,
    ...itemCategories,
    currency: getCurrencyFromRegion(),
    item_list_id:null,
  };
};
export const sendAddToCartEvent = (product, productItemExtraData, view) => {
  let discount = product?.badgeDetails?.offerBadge;
  if (product?.badgeDetails?.offerBadge && product?.specialPrice && product?.specialPrice > 0 && product?.price) {
    discount = product?.price - product?.specialPrice;
  }
  const productItemData = {
    currency: product?.currency,
    value: product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(product?.price),
    section_name: productItemExtraData?.section_name,
    product_type: ASTER_PRODUCT_TYPES.PHARMACY,
    product_sku: product?.sku,
    delivery_promise: product?.delivery_by_V2 || product?.deliverByV2 || product?.deliverBy,
    page_type: view?.page_type,
    screen_name: view?.screen_name,
    ecommerce: {
      items: [
        {
          ...getProductItemObject(product),
          item_list_id: productItemExtraData?.item_list_id,
          item_list_name: productItemExtraData?.item_list_name,
          index: product?.index,
        },
      ],
    },
  };

  if (product instanceof Array) {
    const dataItems = product.map((element) => {
      return {
        ...getProductItemObject(element),
        item_list_id: productItemExtraData?.item_list_id,
        item_list_name: productItemExtraData?.item_list_name,
        index: productItemExtraData?.index,
      };
    });
    productItemData.ecommerce = {
      items: dataItems,
    };
  } else {
    productItemData.ecommerce = {
      items: [
        {
          ...getProductItemObject(product),
          item_list_id: productItemExtraData?.item_list_id,
          item_list_name: productItemExtraData?.item_list_name,
          index: productItemExtraData?.index,
        },
      ],
    };
  }
  dataLayerSendEvent('add_to_cart', productItemData);
  sendAdjustAddToCartEvent(ADJUST_EVENT_TOKENS.ADD_TO_CART_PHARMACY, productItemData);
  const properties = {
    type: 'productItemData',
  };
  sendEventToSmartLook('add_to_cart', properties);
};

export const sendRemoveFromCartEvent = (product, productItemExtraData, deliveryBy) => {
  const serializedAppointment = JSON.parse(sessionStorage.getItem('serializedAppointment'));
  const page_type= sessionStorage.getItem('pageType')?.toLowerCase();
  const screen_name= sessionStorage.getItem('screenName');
  const referrer_page= sessionStorage.getItem('referrerPage');

  const productItemData = {
    currency: product?.currency || 'AED',
    value: product?.sale_price || product?.base_price || parseFloat(product?.specialPrice) || parseFloat(product?.special_price) || parseFloat(product?.price),
    product_name: product?.name,
    product_type: ASTER_PRODUCT_TYPES.PHARMACY,
    product_sku: product?.sku,
    delivery_promise: deliveryBy || product?.deliverByV2 || product?.deliverBy,
    bundle_name: null,
    total_discount: 0,
    availability: product?.outOfStock ? 'out of stock' : 'in stock',
    express_availability: product?.express_bool || product?.express ? 'true' : 'false',
    promotion_value_tag: product?.badgeDetails?.offerBadge ? `${product?.badgeDetails?.offerBadge}% Off!` : null,
    bundle_deal_availability: product?.isBundled ? true : false,
    promotion_name: productItemExtraData?.promotion_name,
    myaster_category: MY_ASTER_CATEGORY.AE,
    section_name:
      productItemExtraData?.section_name === 'Product Listing page' || productItemExtraData?.section_name === 'Product Details Page' || productItemExtraData?.section_name === 'Product Details Page - FBT' ? null : productItemExtraData?.section_name,
    page_type: page_type ? page_type : productItemExtraData?.page_type?.toLowerCase(),
    screen_name: screen_name ? screen_name : productItemExtraData?.screen_name,
    referrer_page: referrer_page ? referrer_page : productItemExtraData?.referrer_page,
    ...(serializedAppointment?.doctorSpecialty? { speciality: serializedAppointment?.doctorSpecialty?.toLowerCase() } : {}),
    ...(serializedAppointment?.consultationType  ? { appointment_mode : serializedAppointment?.consultationType?.toLowerCase() } : {}),
    ...(serializedAppointment?.doctorName ? { doctor_name: serializedAppointment?.doctorName?.toLowerCase() } : {}),
    ...(serializedAppointment?.facilityName ? { facility_name: serializedAppointment?.facilityName?.toLowerCase() } : {}),
  };

  if (product instanceof Array) {
    const dataItems = product.map((element) => {
      return {
        ...getProductItemObject(element),
        item_list_id: productItemExtraData?.item_list_id,
        item_list_name: productItemExtraData?.item_list_name,
        index: productItemExtraData?.index,
      };
    });
    productItemData.value = product?.reduce((total, item) => Number(total) + Number(item.price));
    productItemData.currency = product?.[0]?.currency;
    productItemData.ecommerce = {
      items: dataItems,
    };
  } else {
    productItemData.ecommerce = {
      items: [
        {
          ...getProductItemObject(product),
          item_list_id: productItemExtraData?.item_list_id,
          item_list_name: productItemExtraData?.item_list_name,
          index: productItemExtraData?.index,
        },
      ],
    };
  }
  dataLayerSendEvent('remove_from_cart', productItemData);
  const properties = {
    type: productItemData,
  };
  sendEventToSmartLook('remove_from_cart', properties);
};

export const shipmentChangePopupEvent = (screen_name, page_type, myaster_category, popup_type) => {
  const shipmentParams = {
    screen_name: screen_name,
    page_type: page_type,
    myaster_category: myaster_category,
    popup_type: popup_type
  }
  const properties = {
    type: shipmentParams,
  };
  if(popup_type) {
    dataLayerSendEvent('shipment_change_popup_view', shipmentParams);
    sendEventToSmartLook('shipment_change_popup_view', properties);
  }
}

export const sendProductViewEvent = (product, productItemExtraData, type) => {
  const serializedAppointment = JSON.parse(sessionStorage.getItem('serializedAppointment'));
  const page_type= sessionStorage.getItem('pageType');
  const screen_name= sessionStorage.getItem('screenName');
  const referrer_page = sessionStorage.getItem('referrerPage');

  const productClickData = {
    ecommerce: {
      items: [
        {
          ...getProductItemObjectSelect(product),
          item_list_id: productItemExtraData?.item_list_id,
          index: productItemExtraData?.index,
        },
      ],
    },
    screen_name: screen_name || productItemExtraData?.screen_name,
    section_name: productItemExtraData?.section_name,
    page_id:productItemExtraData?.page_id,
    page_type: page_type || productItemExtraData?.page_type,
    availability: product?.outOfStock || !product?.inStock ? ASTER_PRODUCT_AVAILABILITY.OUT_OF_STOCK : ASTER_PRODUCT_AVAILABILITY.IN_STOCK,
    express_availability: product?.express_bool ? 'true' : 'false',
    promotion_name:productItemExtraData?.promotion_name,
    promotion_value_tag:productItemExtraData?.promotion_value_tag,
    product_name: product?.name,
    product_type: product?.productType || '',
    product_sku: product?.sku,
    delivery_promise: product?.deliverByV2 || product?.deliverBy || null,
    bundle_deal_availability: product?.isBundled ? 'true' :'false',
    referrer_page: referrer_page || productItemExtraData?.referrer_page,
    myaster_category: MY_ASTER_CATEGORY.AE,
    ...(serializedAppointment?.doctorSpecialty? { speciality: serializedAppointment?.doctorSpecialty?.toLowerCase() } : {}),
    ...(serializedAppointment?.consultationType  ? { appointment_mode : serializedAppointment?.consultationType?.toLowerCase() } : {}),
    ...(serializedAppointment?.doctorName ? { doctor_name: serializedAppointment?.doctorName?.toLowerCase() } : {}),
    ...(serializedAppointment?.facilityName ? { facility_name: serializedAppointment?.facilityName?.toLowerCase() } : {}),
    currency: getCurrencyFromRegion(),
  };
  if(serializedAppointment || page_type || screen_name || referrer_page){
    sessionStorage.removeItem('serializedAppointment');
    sessionStorage.removeItem('pageType');
    sessionStorage.removeItem('screenName');
    sessionStorage.removeItem('referrerPage');
  }

  dataLayerSendEvent('select_item', productClickData);
  const properties = {
    type: productClickData,
  };
  sendEventToSmartLook('select_item', properties);
};

export const wishlistAddRemoveGAEvent = (eventName, product, productItemExtraData) => {
  let productItemData = {};
  if (product instanceof Array) {
    productItemData.value = product?.reduce((total, item) => Number(total) + Number(item.price));
    productItemData.currency = product?.[0]?.currency;
    const dataItems = product.map((element) => {
      return {
        ...getProductItemObject(element),
        availability: element?.outOfStock
          ? ASTER_PRODUCT_AVAILABILITY.OUT_OF_STOCK
          : ASTER_PRODUCT_AVAILABILITY.IN_STOCK,
        delivery_promise: element?.deliverBy,
        section_name: productItemExtraData?.item_list_name,
        quantity: 1,
      };
    });
    productItemData.ecommerce = {
      items: dataItems,
    };
  } else {
    productItemData = {
      currency: product?.currency,
      value: product?.specialPrice ? parseFloat(product?.specialPrice) : parseFloat(product?.price),
    };
    productItemData.ecommerce = {
      items: [
        {
          ...getProductItemObject(product),
          availability: product?.outOfStock
            ? ASTER_PRODUCT_AVAILABILITY.OUT_OF_STOCK
            : ASTER_PRODUCT_AVAILABILITY.IN_STOCK,
          delivery_promise: product?.deliverBy,
          section_name: productItemExtraData?.item_list_name,
          quantity: 1,
        },
      ],
    };
  }
  dataLayerSendEvent(eventName, productItemData);
};
